import { Box } from "@mui/material";
import type { QueryObserverResult, RefetchOptions } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { Fragment, type MouseEvent, memo } from "react";
import type { DepartmentLocation, NurseRole, RecurringShift } from "../../../../utils/models";
import { ShiftTypeIcon } from "../../v2/auxillary/ShiftTypeIcon";
import { LocationCell } from "../../v2/Cells/LocationCell";
import { GridDivider } from "../../v2/Grid/GridDivider";
import { gridCellShiftTimesStyle, type ScheduleData } from "../../v2/utils";
import { ConsecutiveCell } from "../Cells/ConsecutiveCell";
import { CYCLE_BLOCKS_SHOWN } from "../consts";

type Props = {
	scheduleData: ScheduleData;
	/** Shared CSS grid column template (from the grid, derived from the selected period). */
	gridTemplateColumns: string;
	/** ISO date (yyyy-MM-dd) of "today" in the dept timezone — used to highlight the current-day column. */
	todayISO: string | null;
	departmentSpecificRoles: NurseRole[];
	uniqueSections: string[];
	refetchRecurringShifts: (options?: RefetchOptions) => Promise<QueryObserverResult<RecurringShift[], Error>>;
	onShiftClick: (recurringShift: RecurringShift, event: MouseEvent<HTMLElement>, clickedDate: DateTime, clickedLocation: DepartmentLocation) => void;
	onShiftKeyDown: (recurringShift: RecurringShift, event: React.KeyboardEvent<HTMLElement>, clickedDate: DateTime, clickedLocation: DepartmentLocation) => void;
	handleOnAddClick: (clickedDate: DateTime, clickedLocation: DepartmentLocation) => void;
};

/**
 * A single location row: `Location | Shift | 6 day cells (block 1) | divider | 6 day cells (block 2)`.
 * Memoized — the cell contents do the heavy per-nurse work, and rows re-render only when their
 * `scheduleData` reference changes (the builder rebuilds on filter/data change).
 */
export const ConsecutiveRow = memo<Props>(
	({
		scheduleData,
		gridTemplateColumns,
		todayISO,
		departmentSpecificRoles,
		uniqueSections,
		refetchRecurringShifts,
		onShiftClick,
		onShiftKeyDown,
		handleOnAddClick,
	}) => {
		const currentLocation = scheduleData.location;
		return (
			<Box sx={{ display: "grid", gridTemplateColumns, gap: "1px", width: "100%", height: "100%", alignItems: "stretch" }}>
				<LocationCell
					refreshRecurringShifts={refetchRecurringShifts}
					currentLocation={currentLocation}
					departmentSpecificRoles={departmentSpecificRoles}
					isLocationOutsideOfConfig={scheduleData.isLocationOutsideOfConfig ?? false}
					weeklyShifts={scheduleData.weeklyShifts}
					uniqueSections={uniqueSections}
					isSynergyMS={false}
				/>
				<Box sx={gridCellShiftTimesStyle}>
					{DateTime.fromFormat(currentLocation.shift_start.substring(0, 5), "HH:mm").toFormat("h:mma")} -{" "}
					{DateTime.fromFormat(currentLocation.shift_end.substring(0, 5), "HH:mm").toFormat("h:mma")}
					<ShiftTypeIcon shiftType={scheduleData.shiftType} />
				</Box>
				{scheduleData.weeklyShifts.map((block, blockIndex) => (
					<Fragment key={block.weekNumber}>
						{block.days.map((day) => (
							<ConsecutiveCell
								key={day.dayDate.toISO()}
								day={day}
								cellDepartmentLocation={currentLocation}
								isToday={!!todayISO && day.dayDate.toISODate() === todayISO}
								onShiftClick={onShiftClick}
								onShiftKeyDown={onShiftKeyDown}
								handleOnAddClick={handleOnAddClick}
							/>
						))}
						{blockIndex < CYCLE_BLOCKS_SHOWN - 1 && <GridDivider />}
					</Fragment>
				))}
			</Box>
		);
	},
);
